"use client"

import * as Dialog from "@radix-ui/react-dialog"
import { ChevronDown, Globe, X } from "lucide-react"
import { useTranslations } from "next-intl"
import { Button } from "@/components/ui/button"
import { allLocales } from "@/i18n/routing"

const LOCALE_FLAG_BASE_PATH = "/assets/images/language-flags"
const LANGUAGE_DIALOG_TRIGGER_ID = "language-picker-trigger"
const LANGUAGE_DIALOG_CONTENT_ID = "language-picker-dialog"
const LANGUAGE_DIALOG_TITLE_ID = "language-picker-title"
const LANGUAGE_DIALOG_DESCRIPTION_ID = "language-picker-description"

interface LanguageModalSwicherProps {
	isOpen: boolean
	onOpenChange: (open: boolean) => void
	currentLocale: {
		locale: string
		summary: string
	}
	onLanguageChange: (locale: string) => void
}

export function LanguageModalSwicher({
	isOpen,
	onOpenChange,
	currentLocale,
	onLanguageChange,
}: LanguageModalSwicherProps) {
	const t = useTranslations("navigation")

	return (
		<Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
			<Dialog.Trigger asChild>
				<Button
					variant="ghost"
					size="sm"
					id={LANGUAGE_DIALOG_TRIGGER_ID}
					className="text-teal-700 hover:text-teal-800"
					aria-label={t("language_picker.open_aria")}
					aria-controls={LANGUAGE_DIALOG_CONTENT_ID}
				>
					<Globe className="h-4 w-4 me-2" />
					{currentLocale.summary}
					<ChevronDown className="h-4 w-4 ms-2" />
				</Button>
			</Dialog.Trigger>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:hidden data-[state=closed]:pointer-events-none" />
				<Dialog.Content
					id={LANGUAGE_DIALOG_CONTENT_ID}
					aria-labelledby={LANGUAGE_DIALOG_TITLE_ID}
					aria-describedby={LANGUAGE_DIALOG_DESCRIPTION_ID}
					className="fixed left-1/2 top-1/2 z-50 w-[min(92vw,420px)] -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-white p-4 shadow-xl outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:hidden data-[state=closed]:pointer-events-none"
				>
					<div className="flex items-start justify-between gap-4">
						<div>
							<Dialog.Title id={LANGUAGE_DIALOG_TITLE_ID} className="text-base font-semibold text-teal-900">
								{t("language_picker.title")}
							</Dialog.Title>
							<Dialog.Description id={LANGUAGE_DIALOG_DESCRIPTION_ID} className="mt-1 text-sm text-slate-600">
								{t("language_picker.description")}
							</Dialog.Description>
						</div>
						<Dialog.Close asChild>
							<Button
								variant="ghost"
								size="sm"
								className="text-teal-700 hover:text-teal-800"
								aria-label={t("language_picker.close_aria")}
								onClick={() => onOpenChange(false)}
							>
								<X className="h-4 w-4" />
							</Button>
						</Dialog.Close>
					</div>
					<div className="mt-4 grid gap-1">
						{allLocales.map((localeOption) => (
							<button
								type="button"
								className={
									"flex w-full items-center justify-between rounded-md px-3 py-2 text-start text-sm transition-colors hover:bg-teal-50 focus:bg-teal-50 focus:outline-none " +
									(localeOption.locale === currentLocale.locale ? "bg-teal-50 text-teal-800 font-semibold" : "text-slate-700")
								}
								key={localeOption.locale}
								onClick={() => {
									onOpenChange(false)
									onLanguageChange(localeOption.locale)
								}}
							>
								<span className="flex items-center gap-2">
									<img
										src={`${LOCALE_FLAG_BASE_PATH}/${localeOption.locale}.svg`}
										alt=""
										aria-hidden="true"
										loading="lazy"
										className="h-4 w-[22px] rounded-[2px] object-cover ring-1 ring-black/10"
									/>
									<span>{localeOption.text}</span>
								</span>
								<span className="text-xs uppercase tracking-wide text-slate-500">{localeOption.summary}</span>
							</button>
						))}
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	)
}
